import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Check, AlertCircle, Mail } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { toast } from "sonner";
import ShweebumCat from "../components/ShweebumCat";
import { API } from "../lib/api";

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const navigate = useNavigate();

  // Console logging for debugging - users can copy/paste these if needed
  const logDebug = (type, message, details = null) => {
    const timestamp = new Date().toISOString();
    const logEntry = { timestamp, type, message, details };
    console.log(`[Shweebum Debug] ${type.toUpperCase()}: ${message}`, details || '');
    
    // Store in sessionStorage for easy access
    const logs = JSON.parse(sessionStorage.getItem('shweebum_debug_logs') || '[]');
    logs.push(logEntry);
    sessionStorage.setItem('shweebum_debug_logs', JSON.stringify(logs.slice(-50))); // Keep last 50 logs
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    // Clear previous logs
    sessionStorage.setItem('shweebum_debug_logs', '[]');
    
    logDebug('info', 'Starting password reset request', { email, endpoint: `${API}/auth/forgot-password` });
    
    try {
      const startTime = Date.now();
      const response = await axios.post(`${API}/auth/forgot-password`, { email });
      const duration = Date.now() - startTime;
      
      logDebug('success', `Server responded in ${duration}ms`, response.data);
      
      // Log the actual status for debugging (console only)
      if (response.data.status) {
        console.log(`[Shweebum] Email status: ${response.data.status}`);
        logDebug('info', `Email status: ${response.data.status}`);
      }
      
      setSent(true);
      toast.success("Check your email for reset instructions");
    } catch (error) {
      logDebug('error', 'Request failed', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
      logDebug('info', 'Request finished');
    }
  };

  // Function to copy debug logs - can be triggered from console
  window.copyShweebumLogs = () => {
    const logs = sessionStorage.getItem('shweebum_debug_logs') || '[]';
    navigator.clipboard.writeText(logs);
    console.log('Debug logs copied to clipboard!');
    return 'Logs copied! Paste them when contacting support.';
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Card>
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <ShweebumCat size="md" />
            </div>
            <CardTitle className="font-literary text-2xl">
              {sent ? "Check Your Email" : "Forgot Password"}
            </CardTitle>
            <CardDescription>
              {sent 
                ? "We sent you a link to reset your password."
                : "Enter your email and we'll send you a reset link"
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            {sent ? (
              <div className="space-y-4">
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <Check className="w-12 h-12 text-green-600 mx-auto mb-2" />
                  <p className="text-green-700 font-medium">Email sent successfully!</p>
                </div>
                
                {/* Spam folder notice */}
                <div className="p-4 bg-slate-800/30 border border-cyan-500/25 rounded-lg">
                  <div className="flex items-start gap-3">
                    <Mail className="w-5 h-5 text-cyan-400 mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-cyan-200">
                      <p className="font-medium mb-1">Don't see the email?</p>
                      <ul className="list-disc list-inside space-y-1 text-cyan-300">
                        <li>Check your <strong>Spam</strong> or <strong>Junk</strong> folder</li>
                        <li>Add <strong>noreply@shweebum.com</strong> to your contacts</li>
                        <li>Wait a few minutes and refresh</li>
                      </ul>
                    </div>
                  </div>
                </div>
                
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => navigate("/")}
                  data-testid="back-to-login-btn"
                >
                  Back to Login
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="forgot-email-input"
                  />
                </div>
                <Button 
                  type="submit" 
                  className="w-full bg-cyan-700 hover:bg-cyan-800"
                  disabled={loading}
                  data-testid="forgot-submit-btn"
                >
                  {loading ? "Sending..." : "Send Reset Link"}
                </Button>
                <Button 
                  type="button"
                  variant="ghost" 
                  className="w-full"
                  onClick={() => navigate("/")}
                >
                  Back to Login
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
